import { useGetAllOperationType } from "@features/ax/srvc-and-mgmt/service-order/hooks";
import { SopFilterPanelFilters } from "@features/ax/srvc-and-mgmt/service-order/types";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import React, { useEffect, useMemo, useState } from "react";

interface SOPFilters {
  serviceAssociation: string;
  operationType: string;
}

interface SOPFilterPanelProps {
  currentFilters: SopFilterPanelFilters;
  onApplyFilters: (filters: Partial<SOPFilters>) => void;
  onResetFilters: () => void;
}

export const SOPFilterPanel: React.FC<SOPFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<{
    serviceAssociation: string;
    operationType: string;
  }>({
    serviceAssociation:
      currentFilters?.serviceAssociation || "ALL_SERVICE_TYPES",
    operationType: currentFilters?.operationType || "ALL_OPERATION_TYPES",
  });

  // Service Association options
  const serviceAssociationOptions = [
    { label: "All Service Types", value: "ALL_SERVICE_TYPES" },
    { label: "Assets", value: "assets" },
    { label: "Systems", value: "systems" },
    { label: "Facilities", value: "facilities" },
    { label: "Networks", value: "networks" },
  ];
  const { data: operationTypeList } = useGetAllOperationType();

  const operationTypeOptions = useMemo(() => {
    return (
      operationTypeList?.map((assetType: any) => ({
        value: assetType.key,
        label: assetType.value,
      })) || []
    );
  }, [operationTypeList]);

  // Create form fields
  const createFormFields = (): FormField[] => [
    {
      name: "serviceAssociation",
      type: "select",
      label: "Service Association",
      required: false,
      options: serviceAssociationOptions,
      placeholder: "Select service association",
      classes: FIELD_CLASSES,
    },
    {
      name: "operationType",
      type: "select",
      label: "Operation Type",
      required: false,
      options: operationTypeOptions,
      placeholder: "Select operation type",
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: createFormFields(),
    defaultValues: tempFilters,
  });

  // Update form values when current filters change
  useEffect(() => {
    const newTempFilters = {
      serviceAssociation:
        currentFilters?.serviceAssociation || "ALL_SERVICE_TYPES",
      operationType: currentFilters?.operationType || "ALL_OPERATION_TYPES",
    };

    setTempFilters(newTempFilters);

    // Reset form with new values
    form.reset(newTempFilters);
  }, [currentFilters]);

  // Handle form submission - PREVENT DEFAULT BEHAVIOR
  const handleSubmit = (data: any, event?: React.FormEvent) => {
    // Prevent form submission from reloading the page
    if (event) {
      event.preventDefault();
    }

    const filtersToApply: Partial<SOPFilters> = {};

    console.log("Create :", filtersToApply);
    if (
      data.serviceAssociation &&
      data.serviceAssociation !== "ALL_SERVICE_TYPES"
    ) {
      filtersToApply.serviceAssociation = data.serviceAssociation;
    }
    if (data.operationType && data.operationType !== "ALL_OPERATION_TYPES") {
      filtersToApply.operationType = data.operationType;
    }

    onApplyFilters(filtersToApply);
  };

  // Handle apply button click - PREVENT DEFAULT AND TRIGGER APPLY
  const handleApplyClick = (event: React.MouseEvent) => {
    event.preventDefault(); // Prevent form submission
    const currentFormData = form.getValues();
    handleSubmit(currentFormData);
  };

  // Handle reset
  const handleReset = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault(); // Prevent any default behavior
    }

    const defaultFilters = {
      serviceAssociation: "ALL_SERVICE_TYPES",
      operationType: "ALL_OPERATION_TYPES",
    };
    setTempFilters(defaultFilters);
    form.reset(defaultFilters);
    onResetFilters();
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    setTempFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Custom footer with Apply and Reset buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleReset}>
        Reset Filters
      </Button>
      <Button
        type="button" // Changed from "submit" to "button"
        onClick={handleApplyClick} // Use custom click handler
      >
        Apply Filters
      </Button>
    </div>
  );

  return (
    <DynamicForm
      fields={createFormFields()}
      form={form}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onFieldChange={handleFieldChange}
      config={{
        showSubmitButton: false, // We're using custom footer
        showResetButton: false, // We're using custom footer
        layout: "vertical",
        spacing: "medium",
      }}
      gridColumns={2}
      responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
      className="space-y-6"
      renderCustomFooter={renderCustomFooter}
    />
  );
};
